// manage-leave-policies.component.ts
// HR-only view: manage leave types (create / edit / delete) and policy documents (upload / delete).
// Extracted from LeavePoliciesComponent — no isEmployee branching.

import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ColDef } from 'ag-grid-enterprise';
import { dateFilterParamsLong } from '@rcs/utils';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { of } from 'rxjs';
import { ErsGridComponent } from '@rcs/grid';
import { LmLoadingOverlayComponent } from '../lm-loading-overlay/lm-loading-overlay.component';
import { DeleteDialogComponent } from '../../dialogs/delete-dialog/delete-dialog.component';
import { UploadLeavePolicyDialogComponent, UploadLeavePolicyDialogResult } from '../../dialogs/upload-leave-policy-dialog/upload-leave-policy-dialog.component';
import { PolicyDocument, AllocatedLeave } from '../../models/leave.model';
import { leaveTypeRenderer, statusBadgeRenderer, daysRenderer, dashIfNull, dateRenderer, docNameRenderer } from '../../utils/grid-cell-renderers';
import { formatDateLong, parseLocalDate } from '../../utils/date.utils';
import { LmActionCellComponent } from '../lm-action-cell/lm-action-cell.component';
import { LmDocActionCellComponent } from '../lm-doc-action-cell/lm-doc-action-cell.component';
import { DocPreviewDialogComponent, DocPreviewDialogData } from '@rcs/doc-viewer';
import { LeaveService } from '../../services/leave.service';
import { TranslocoPipe } from '@jsverse/transloco';

const AUTO_HEIGHT_GRID = {
  suppressCellFocus: true,
  pagination: false,
  suppressPaginationPanel: true,
} as const;

@Component({
  selector: 'ers-manage-leave-policies',
  standalone: true,
  imports: [
    TranslocoPipe,
    CommonModule,
    FormsModule,
    RouterModule,
    MatDialogModule,
    ErsGridComponent,
    LmActionCellComponent,
    LmDocActionCellComponent,
    LmLoadingOverlayComponent,
  ],
  templateUrl: './manage-leave-policies.component.html',
  styleUrls: ['./manage-leave-policies.component.scss'],
})
export class ManageLeavePoliciesComponent implements OnInit {
  private leaveService = inject(LeaveService);
  private dialog       = inject(MatDialog);
  private router       = inject(Router);

  selectedYear   = new Date().getFullYear();
  availableYears = [2023, 2024, 2025, 2026];
  allocatedLeaves: AllocatedLeave[] = [];
  leavesLoading = false;
  docsLoading = false;
  isSaving = false;
  policyDocuments: PolicyDocument[] = [];

  private tenantId: string | null = null;

  // ─── Leave-types grid ──────────────────────────────────────────────────────

  leavesColDefs: ColDef[] = [
    { headerName: 'Leave Type',    field: 'leaveType',    flex: 2, filter: 'agSetColumnFilter', cellRenderer: leaveTypeRenderer },
    { headerName: 'State',         field: 'state',        flex: 1, minWidth: 100, filter: 'agSetColumnFilter', cellRenderer: statusBadgeRenderer },
    { headerName: 'Available',     field: 'available',    flex: 1, minWidth: 90, filter: 'agSetColumnFilter', cellRenderer: daysRenderer },
    { headerName: 'Carry Forward', field: 'carryForward', flex: 1, minWidth: 100, cellStyle: { textAlign: 'center', fontSize: '13px' }, cellRenderer: dashIfNull },
    { headerName: 'Unpaid',        field: 'unpaid',       flex: 0.8, minWidth: 80, cellStyle: { textAlign: 'center', fontSize: '13px' }, cellRenderer: dashIfNull },
    { headerName: 'Valid Till',    field: 'validTill',    flex: 1, filter: 'agSetColumnFilter', cellStyle: { fontSize: '13px' }, cellRenderer: dateRenderer },
    { headerName: 'Action',        field: 'action',       flex: 0.5, minWidth: 60, sortable: false, cellRenderer: LmActionCellComponent },
  ];

  leavesGridOptions = {
    suppressCellFocus: true,
    suppressHorizontalScroll: true,
    pagination: true,
    paginationPageSize: 5,
    paginationPageSizeSelector: false,
    rowHeight: 44,
  };

  // ─── Policy-documents grid ─────────────────────────────────────────────────

  docColDefs: ColDef[] = [
    { headerName: 'Name',          field: 'name',         flex: 2, filter: 'agSetColumnFilter', cellRenderer: docNameRenderer },
    { headerName: 'Uploaded By',   field: 'uploadedBy',   flex: 2, filter: 'agSetColumnFilter', cellStyle: { fontSize: '13px' } },
    { headerName: 'File Size',     field: 'fileSize',     flex: 1, minWidth: 90, cellStyle: { fontSize: '13px' } },
    {
      headerName: 'Uploaded Date', field: 'uploadedDate', flex: 1, minWidth: 100, filter: 'agDateColumnFilter', filterParams: dateFilterParamsLong, cellStyle: { fontSize: '13px' },
      // Default: most recently modified on top
      sort: 'desc', sortIndex: 0,
      comparator: (a: string, b: string) => {
        const ta = a && a !== '\u2013' ? new Date(a).getTime() : 0;
        const tb = b && b !== '\u2013' ? new Date(b).getTime() : 0;
        return ta - tb;
      },
    },
    { headerName: 'Action',        field: 'action',       flex: 0.5, minWidth: 60, sortable: false, cellRenderer: LmDocActionCellComponent },
  ];

  docGridOptions = {
    suppressCellFocus: true,
    suppressHorizontalScroll: true,
    pagination: true,
    paginationPageSize: 5,
    paginationPageSizeSelector: false,
    rowHeight: 40,
  };

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.leaveService.selectedUserTenant$.pipe(take(1)).subscribe(tenant => {
      if (tenant?.id) this.tenantId = tenant.id;
    });
    this.loadAllocatedLeaves();
    this.loadDocuments();
  }

  // ─── Computed ──────────────────────────────────────────────────────────────

  get totalLeavesYearly(): number {
    return this.allocatedLeaves.reduce((sum, l) => sum + l.available, 0);
  }

  onYearChange(): void {
    this.loadAllocatedLeaves();
  }

  // ─── Navigation ────────────────────────────────────────────────────────────

  onCreateNewLeave(): void {
    this.router.navigate(['/ers/leave-management/create-leave']);
  }

  // ─── Grid cell click ───────────────────────────────────────────────────────

  onLeavesGridCellClick(event: any): void {
    const mouseEvent = event?.event as MouseEvent | undefined;
    if (!mouseEvent) return;
    const target = (mouseEvent.target as HTMLElement)?.closest?.('[data-action]') as HTMLElement | null;
    if (!target) return;
    const action = target.getAttribute('data-action');
    const row    = event?.data as AllocatedLeave | undefined;
    if (!action || !row?.id) return;
    if (action === 'edit')   this.router.navigate(['/ers/leave-management/create-leave', row.id]);
    if (action === 'delete') this.openDeleteLeaveTypeDialog(row);
  }

  onDocGridCellClick(event: any): void {
    const mouseEvent = event?.event as MouseEvent | undefined;
    if (!mouseEvent) return;
    const target = (mouseEvent.target as HTMLElement)?.closest?.('[data-action]') as HTMLElement | null;
    if (!target) return;
    const action = target.getAttribute('data-action');
    const doc    = this.policyDocuments.find(d => String(d.id) === target.getAttribute('data-id'));
    if (!doc) return;
    if (action === 'preview')  this.openDocPreview(doc);
    if (action === 'delete')   this.openDeleteDocDialog(doc);
    if (action === 'download') this.downloadDoc(doc);
  }

  // ─── Upload ────────────────────────────────────────────────────────────────

  onUploadClick(): void {
    const leaveTypesData = this.allocatedLeaves
      .filter(l => l.id)
      .map(l => ({ value: l.id, label: l.leaveType }));

    this.dialog.open(UploadLeavePolicyDialogComponent, {
      width: '760px',
      panelClass: 'lm-dialog-panel',
      disableClose: false,
      autoFocus: false,
      data: { leaveTypes: leaveTypesData },
    }).afterClosed().subscribe((result: UploadLeavePolicyDialogResult | null) => {
      if (!result?.files?.length) return;
      const leaveTypeId = result.leaveTypes?.[0] ?? null;
      result.files.forEach(file => this.uploadAndSave(file, leaveTypeId));
    });
  }

  // ─── Private helpers ───────────────────────────────────────────────────────

  private loadAllocatedLeaves(): void {
    this.leavesLoading = true;
    this.leaveService.getAllLeaveTypesManagement().subscribe({
      next: (types) => {
        const year = this.selectedYear;
        const yearStart = `${year}-01-01`;
        const yearEnd   = `${year}-12-31`;

        const filtered = types.filter(t => {
          const start = t.leavePeriodStartDate ?? null;
          const end   = t.validTill ?? null;
          // If no date info, include in results (no date to filter by)
          if (!start && !end) return true;
          // If only start exists (no validTill), check start falls in the year
          if (start && !end) return start.slice(0, 4) === String(year);
          // If only end exists, check end hasn't passed before year starts
          if (!start && end) return end >= yearStart;
          // Both exist: check overlap with the year range
          return start! <= yearEnd && end! >= yearStart;
        });

        this.allocatedLeaves = filtered.map(t => ({
          id:           t.id,
          leaveType:    t.leaveTypeName,
          shortCode:    t.leaveTypeName.slice(0, 2).toUpperCase(),
          color:        '',
          state:        t.isActive ? 'Active' : 'Inactive',
          available:    t.quantityAllocatedForEachPeriod,
          carryForward: t.carryForwardDays ?? null,
          unpaid:       null,
          validTill:    t.validTill ?? null,
        } as AllocatedLeave));
        this.leavesLoading = false;
      },
      error: () => { this.leavesLoading = false; },
    });
  }

  private computeValidTill(startDate?: string | Date | null, months?: number | null): string | null {
    if (!startDate || !months) return null;
    const start = parseLocalDate(startDate);
    if (isNaN(start.getTime())) return null;
    const end = new Date(start);
    // validTill = last inclusive day (start + months - 1 day)
    end.setMonth(end.getMonth() + months);
    end.setDate(end.getDate() - 1);
    const y = end.getFullYear();
    const m = String(end.getMonth() + 1).padStart(2, '0');
    const d = String(end.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private loadDocuments(): void {
    this.docsLoading = true;
    this.leaveService.getPolicyDocuments().subscribe({
      next: (response: any) => {
        this.docsLoading = false;
        const rows: any[] = Array.isArray(response) ? response : (response?.data ?? response?.value ?? []);
        this.policyDocuments = (rows ?? []).map((r: any) => {
          const url      = r.documentUrl || r.documentPath || r.DOCUMENT_URL || r.DOCUMENT_PATH || '';
          const fileName = r.documentTitle || r.DOCUMENT_TITLE || r.name || '';
          const ext      = fileName.split('.').pop()?.toUpperCase() ?? 'FILE';
          const createdOn = r.createdOn || r.CREATED_ON || r.createdAt || this.parseDateFromBlobUrl(url);
          const uploadedBy = r.createdBy || r.CREATED_BY || r.uploadedBy
                          || r.documentDescription || r.DOCUMENT_DESCRIPTION || '–';
          return {
            id:           r.id || r.ID,
            name:         fileName,
            uploadedBy,
            fileSize:     '–',
            fileType:     ext,
            uploadedDate: createdOn
              ? formatDateLong(createdOn)
              : '–',
            documentUrl:  url,
            leaveTypeId:  r.leaveTypeId || r.LEAVE_TYPE_ID || r.LeaveTypeId || null,
          };
        });

        this.policyDocuments.forEach((doc, idx) => {
          if (!doc.documentUrl) return;
          this.leaveService.getPolicyDocumentFileInfo(doc.documentUrl).subscribe({
            next: ({ contentLength }) => {
              if (contentLength == null) return;
              let size: string;
              if (contentLength < 1024)             size = `${contentLength} B`;
              else if (contentLength < 1024 * 1024) size = `${(contentLength / 1024).toFixed(1)} KB`;
              else                                   size = `${(contentLength / (1024 * 1024)).toFixed(1)} MB`;
              this.policyDocuments = this.policyDocuments.map((d, i) =>
                i === idx ? { ...d, fileSize: size } : d
              );
            },
          });
        });
      },
      error: (err) => { this.docsLoading = false; console.error('Failed to load policy documents', err); },
    });
  }

  private parseDateFromBlobUrl(url: string): string | null {
    const match = url.match(/_(\d{4})(\d{2})(\d{2})\d{6,}\.\w+$/);
    if (!match) return null;
    return `${match[1]}-${match[2]}-${match[3]}`;
  }

  private uploadAndSave(file: File, leaveTypeId: string | null = null): void {
    this.isSaving = true;
    const tenantId$ = this.tenantId
      ? of(this.tenantId)
      : this.leaveService.selectedUserTenant$.pipe(
          filter(t => !!t?.id), map(t => t!.id), take(1),
        );

    tenantId$.pipe(
      switchMap(tenantId => this.leaveService.uploadDocument(file, tenantId, 'leaveDocuments')),
      switchMap(uploadRes => {
        const documentTitle = file.name;
        const documentUrl   = uploadRes.fileUrl ?? '';
        return this.leaveService.savePolicyDocument({ documentTitle, documentUrl, leaveTypeId });
      }),
    ).subscribe({
      next: () => { this.isSaving = false; this.loadDocuments(); },
      error: err => { this.isSaving = false; console.error('Policy document upload failed', err); },
    });
  }

  private openDeleteLeaveTypeDialog(row: AllocatedLeave): void {
    this.dialog.open(DeleteDialogComponent, {
      width: '460px',
      panelClass: 'lm-dialog-panel',
      data: { itemName: row.leaveType },
    }).afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.isSaving = true;
      this.leaveService.deleteLeaveType(row.id).subscribe({
        next: () => { this.isSaving = false; this.loadAllocatedLeaves(); },
        error: (err: any) => { this.isSaving = false; console.error('Delete leave type failed', err); },
      });
    });
  }

  private openDocPreview(doc: PolicyDocument): void {
    if (!doc.documentUrl) return;
    this.leaveService.downloadProxiedDocument(doc.documentUrl).subscribe({
      next: (blob) => {
        this.dialog.open<DocPreviewDialogComponent, DocPreviewDialogData>(
          DocPreviewDialogComponent,
          { data: { src: blob, fileName: doc.name || 'document' }, maxWidth: '95vw', panelClass: 'lm-dialog-panel' }
        );
      },
      error: () => window.open(doc.documentUrl, '_blank', 'noopener,noreferrer'),
    });
  }

  private openDeleteDocDialog(doc: PolicyDocument): void {
    this.dialog.open(DeleteDialogComponent, {
      width: '460px',
      panelClass: 'lm-dialog-panel',
      data: { itemName: doc.name },
    }).afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.isSaving = true;
      this.leaveService.deletePolicyDocument(String(doc.id)).subscribe({
        next: () => { this.isSaving = false; this.loadDocuments(); },
        error: err => { this.isSaving = false; console.error('Delete policy document failed', err); },
      });
    });
  }

  private downloadDoc(doc: PolicyDocument): void {
    if (!doc.documentUrl) return;
    this.leaveService.downloadProxiedDocument(doc.documentUrl).subscribe({
      next: (blob) => this.triggerBlobDownload(blob, doc.name || 'document'),
      error: () => window.open(doc.documentUrl, '_blank', 'noopener,noreferrer'),
    });
  }

  private triggerBlobDownload(blob: Blob, fileName: string): void {
    const blobUrl = window.URL.createObjectURL(blob);
    const anchor  = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = fileName;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    setTimeout(() => { document.body.removeChild(anchor); window.URL.revokeObjectURL(blobUrl); }, 100);
  }
}
